import { useState } from 'react';
import { assessmentAPI, ApiResult } from '../../../../services/api';

interface TimeSlot {
  id: string;
  date: string;
  time: string;
  datetime: Date;
  available: boolean;
}

interface UseSMEBookingReturn {
  showSMEBooking: boolean;
  setShowSMEBooking: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTimeSlot: TimeSlot | null;
  setSelectedTimeSlot: React.Dispatch<React.SetStateAction<TimeSlot | null>>;
  bookingLoading: boolean;
  generateTimeSlots: () => TimeSlot[];
  bookSMESession: (contactData: Record<string, unknown>) => Promise<ApiResult>;
}

export const useSMEBooking = (): UseSMEBookingReturn => {
  const [showSMEBooking, setShowSMEBooking] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  const generateTimeSlots = (): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(9, 0, 0, 0);
    
    // Generate slots for the next 30 days
    for (let day = 0; day < 30; day++) {
      const currentDay = new Date(startOfDay);
      currentDay.setDate(currentDay.getDate() + day);
      
      // Skip weekends
      if (currentDay.getDay() === 0 || currentDay.getDay() === 6) continue;
      
      // Generate hourly slots from 9 AM to 5 PM
      for (let hour = 9; hour < 17; hour++) {
        const slotTime = new Date(currentDay);
        slotTime.setHours(hour, 0, 0, 0);
        
        // Skip past slots
        if (slotTime <= now) continue;
        
        slots.push({
          id: `${currentDay.toISOString().split('T')[0]}-${hour}`,
          date: currentDay.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          time: `${hour}:00 - ${hour + 1}:00`,
          datetime: slotTime,
          available: Math.random() > 0.3 // Random availability for demo
        });
      }
    }
    
    return slots.filter(slot => slot.available).slice(0, 20); // Return first 20 available slots
  };

  const bookSMESession = async (contactData: Record<string, unknown>): Promise<ApiResult> => {
    if (!selectedTimeSlot) return { success: false, error: 'No time slot selected' };
    
    setBookingLoading(true);
    try {
      const bookingData = {
        ...contactData,
        timeSlot: selectedTimeSlot,
        type: 'sme_consultation'
      };
      
      const api = assessmentAPI as typeof assessmentAPI & { bookSMESession: (data: Record<string, unknown>) => Promise<ApiResult> };
      const result = await api.bookSMESession(bookingData);
      if (result.success) {
        setShowSMEBooking(false);
        setSelectedTimeSlot(null);
        return result;
      } else {
        throw new Error(result.error || 'Failed to book SME session');
      }
    } catch (error) {
      console.error('Error booking SME session:', error);
      throw error;
    } finally {
      setBookingLoading(false);
    }
  };

  return {
    showSMEBooking,
    setShowSMEBooking,
    selectedTimeSlot,
    setSelectedTimeSlot,
    bookingLoading,
    generateTimeSlots,
    bookSMESession
  };
};

export default useSMEBooking;
